
import { useState, useEffect, useCallback } from 'react';
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export function useMapApiKey(apiKeyOverride?: string) {
  const [apiKey, setApiKey] = useState<string | null>(apiKeyOverride || null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const MAX_RETRIES = 2;

  const fetchApiKey = useCallback(async () => {
    if (apiKeyOverride) {
      setApiKey(apiKeyOverride);
      setIsLoading(false);
      return;
    }

    try {
      console.log('Fetching Google Maps API key');
      setIsLoading(true);
      
      // Use Supabase invoke method for better security and error handling
      const { data, error } = await supabase.functions.invoke('get-google-maps-key', {
        // Send minimal data to prevent injection attacks
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      if (error || !data || data.error) {
        console.error('Error fetching Google Maps API key:', error || data?.error);
        setHasError(true);
        setIsLoading(false);
        
        if (retryCount < MAX_RETRIES) {
          toast.error("Map service temporarily unavailable. Retrying...");
          setRetryCount(prev => prev + 1);
          setTimeout(() => fetchApiKey(), 2000 * (retryCount + 1)); // Exponential backoff
        } else {
          toast.error("Could not load the map. Please try again later.");
        }
        return;
      }

      if (data.apiKey) {
        console.log('Successfully retrieved API key');
        // Validate the API key format before setting it
        if (typeof data.apiKey === 'string' && data.apiKey.length > 10) {
          setApiKey(data.apiKey);
          setHasError(false);
        } else {
          console.error('Invalid API key format received');
          setHasError(true);
          toast.error("Map service configuration issue. Please contact support.");
        }
      } else {
        console.error('No API key returned from edge function');
        setHasError(true);
      }
    } catch (err) {
      console.error('Exception when fetching Google Maps API key:', err);
      setHasError(true);
    } finally {
      setIsLoading(false);
    }
  }, [apiKeyOverride, retryCount]);

  useEffect(() => {
    fetchApiKey();
  }, [fetchApiKey]);

  return { 
    apiKey, 
    isLoading, 
    hasError, 
    setHasError,
    retry: fetchApiKey
  };
}
